"use client";

import { useMemo } from "react";

interface SparklineFilledGradientProps {
  data: number[];
  width?: number;
  height?: number;
  className?: string;
}

// Helper to create path from data
function createPath(data: number[], width: number, height: number, padding: number = 3) {
  if (data.length < 2) return "";

  const min = Math.min(...data) * 100;
  const max = Math.max(...data) * 100;
  const range = max - min || 1;

  const effectiveHeight = height - padding * 2;
  const effectiveWidth = width - padding * 2;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * effectiveWidth;
    const normalizedValue = (value * 100 - min) / range;
    const y = padding + effectiveHeight - normalizedValue * effectiveHeight;
    return { x, y };
  });

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` Q ${cpx} ${prev.y} ${cpx} ${(prev.y + curr.y) / 2}`;
  }
  d += ` L ${points[points.length - 1].x} ${points[points.length - 1].y}`;

  return d;
}

// Helper to create area path (for filled variant)
function createAreaPath(data: number[], width: number, height: number, padding: number = 3) {
  if (data.length < 2) return "";

  const min = Math.min(...data) * 100;
  const max = Math.max(...data) * 100;
  const range = max - min || 1;

  const effectiveHeight = height - padding * 2;
  const effectiveWidth = width - padding * 2;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * effectiveWidth;
    const normalizedValue = (value * 100 - min) / range;
    const y = padding + effectiveHeight - normalizedValue * effectiveHeight;
    return { x, y };
  });

  let d = `M ${points[0].x} ${height}`;
  d += ` L ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` Q ${cpx} ${prev.y} ${cpx} ${(prev.y + curr.y) / 2}`;
  }
  d += ` L ${points[points.length - 1].x} ${points[points.length - 1].y}`;
  d += ` L ${points[points.length - 1].x} ${height}`;
  d += ` Z`;

  return d;
}

/**
 * Sparkline with filled gradient area that changes color based on price direction
 * Green when price went up, Red when price went down
 */
export default function SparklineFilledGradient({
  data,
  width = 70,
  height = 28,
  className = ""
}: SparklineFilledGradientProps) {
  const { linePath, areaPath, lineGradientId, areaGradientId, isUp } = useMemo(() => {
    const lineId = `line-gradient-${Math.random().toString(36).slice(2)}`;
    const areaId = `area-gradient-${Math.random().toString(36).slice(2)}`;
    const validData = data.length >= 2 ? data : Array.from({ length: 15 }, () => Math.random() * 0.5 + 0.25);
    const lastValue = validData[validData.length - 1] || 0.5;
    const firstValue = validData[0] || 0.5;
    return {
      linePath: createPath(validData, width, height),
      areaPath: createAreaPath(validData, width, height),
      lineGradientId: lineId,
      areaGradientId: areaId,
      isUp: lastValue >= firstValue
    };
  }, [data, width, height]);

  // Color based on direction - use FrenFlow colors
  const upColor = "#00FF9D";   // FrenFlow green
  const downColor = "#FF1744"; // FrenFlow red
  const color = isUp ? upColor : downColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
    >
      <defs>
        {/* Line gradient: red→green or green→red based on direction */}
        <linearGradient id={lineGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isUp ? downColor : upColor} />
          <stop offset="100%" stopColor={isUp ? upColor : downColor} />
        </linearGradient>
        {/* Area gradient: vertical fade with direction color */}
        <linearGradient id={areaGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path
        d={areaPath}
        fill={`url(#${areaGradientId})`}
      />
      <path
        d={linePath}
        fill="none"
        stroke={`url(#${lineGradientId})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
